import { type Validation } from '@wix/ambassador-forms-v4-form/types';
import { type ValidationError, ErrorTypes } from '@wix/form-validator';
import { deserializeDate } from '../date-picker/helpers';
import { type DateFieldError } from '../date-picker/types';
import { SupportedDateParts } from '../../utils/date-format-parts';

export enum DateFieldErrorSuffix {
  YEAR_MONTH = 'year-month',
  YEAR_DAY = 'year-day',
  MONTH_DAY = 'month-day',
  YEAR = 'year',
  MONTH = 'month',
  DAY = 'day',
}

export interface DateFieldValidationError
  extends Omit<ValidationError, 'errorType' | 'params'> {
  errorType: ErrorTypes.INCOMPLETE_DATE_ERROR;
  params: {
    suffix: DateFieldErrorSuffix;
  };
}

export const getDateFieldError = (
  _validation?: Validation,
  error?: ValidationError,
  value?: string,
) => {
  const isFormatError = error?.errorType === ErrorTypes.FORMAT_ERROR;

  if (!error || !isFormatError) {
    return error;
  }

  const { YEAR: _YEAR, MONTH, DAY } = deserializeDate(value);
  const YEAR = _YEAR?.length === 4;

  // All missing is the error we got as input
  if (!YEAR && !MONTH && !DAY) {
    return error;
  }

  const withErrorSuffix = (
    suffix: DateFieldErrorSuffix,
  ): DateFieldValidationError => ({
    ...error,
    errorType: ErrorTypes.INCOMPLETE_DATE_ERROR,
    params: {
      suffix,
    },
  });

  if (!YEAR && !MONTH) {
    return withErrorSuffix(DateFieldErrorSuffix.YEAR_MONTH);
  }
  if (!YEAR && !DAY) {
    return withErrorSuffix(DateFieldErrorSuffix.YEAR_DAY);
  }
  if (!MONTH && !DAY) {
    return withErrorSuffix(DateFieldErrorSuffix.MONTH_DAY);
  }
  if (!YEAR) {
    return withErrorSuffix(DateFieldErrorSuffix.YEAR);
  }
  if (!MONTH) {
    return withErrorSuffix(DateFieldErrorSuffix.MONTH);
  }
  if (!DAY) {
    return withErrorSuffix(DateFieldErrorSuffix.DAY);
  }

  return error;
};

export const getDatePartsErrors = (
  hasError: boolean,
  value?: string,
): DateFieldError => {
  const { YEAR, MONTH, DAY } = deserializeDate(value);
  const invalidParts = [YEAR?.length !== 4, !MONTH, !DAY];
  const allValidParts = !invalidParts.some(Boolean);

  return {
    [SupportedDateParts.YEAR]: hasError && (allValidParts || invalidParts[0]),
    [SupportedDateParts.MONTH]: hasError && (allValidParts || invalidParts[1]),
    [SupportedDateParts.DAY]: hasError && (allValidParts || invalidParts[2]),
  };
};

const isDateFieldValidationError = (
  error?: DateFieldValidationError | ValidationError,
): error is DateFieldValidationError => {
  return error?.errorType === ErrorTypes.INCOMPLETE_DATE_ERROR;
};

export const getDateValidationErrorSuffix = (
  error?: DateFieldValidationError | ValidationError,
) => (isDateFieldValidationError(error) ? error.params.suffix : undefined);
